import { isValidEmailAddress } from '@/utils/email';

export type AttParams = Record<string, string>;

const STORAGE_KEY = 'attParams';
const SENT_FLAG_KEY = 'attConversionClickSent';
const ATT_PREFIX = 'att_';
const SOURCE_CID_KEYS = ['att_sourceCid', 'att_source_cid'] as const;

const isBrowser = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
const EMBEDDED_ATT_PARAM_RE = /(^|[?&#])(?=att_[A-Za-z0-9_]*=)/i;

function normalizeSearch(search?: string | null): string {
  if (!search) {
    return '';
  }
  return search.startsWith('?') ? search.slice(1) : search;
}

/**
 * Извлекает из query string все параметры с префиксом "att_".
 */
export function extractAttParams(search: string | URLSearchParams): AttParams {
  const searchParams = typeof search === 'string' ? new URLSearchParams(normalizeSearch(search)) : search;
  const result: AttParams = {};

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith(ATT_PREFIX)) {
      const [cleanValue, embeddedSearch] = splitEmbeddedAttParams(value);
      result[key] = cleanValue;

      if (embeddedSearch) {
        const embeddedParams = new URLSearchParams(embeddedSearch);
        for (const [embeddedKey, embeddedValue] of embeddedParams.entries()) {
          if (embeddedKey.startsWith(ATT_PREFIX)) {
            result[embeddedKey] = embeddedValue;
          }
        }
      }
    }
  }

  return result;
}

function splitEmbeddedAttParams(value: string): [string, string | null] {
  const match = EMBEDDED_ATT_PARAM_RE.exec(value);
  if (!match || match.index < 0) {
    return [value, null];
  }

  return [
    value.slice(0, match.index),
    value.slice(match.index + match[1].length),
  ];
}

function hasValue(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function resolveAttributionEmail(
  params: AttParams | null | undefined,
  fallbackEmail?: string | null,
): string | undefined {
  const attributionEmail = params?.att_email?.trim();
  if (attributionEmail && isValidEmailAddress(attributionEmail)) {
    return attributionEmail;
  }

  const fallback = fallbackEmail?.trim();
  return fallback && isValidEmailAddress(fallback) ? fallback : undefined;
}

function getConversionClickPayloadKey(payload: AttParams): string {
  return JSON.stringify(
    Object.keys(payload)
      .sort()
      .map((key) => [key, payload[key]]),
  );
}

export function buildAttConversionClickPayload(
  params: AttParams | null | undefined,
  fallbackEmail?: string | null,
): AttParams | null {
  if (!params || !hasValue(params.att_source)) {
    return null;
  }

  const sourceCid = SOURCE_CID_KEYS.some((key) => hasValue(params[key]));
  if (!sourceCid) {
    return null;
  }

  const email = resolveAttributionEmail(params, fallbackEmail);
  if (!email) {
    return null;
  }

  return {
    ...params,
    att_email: email,
  };
}

/**
 * Сохраняет att_ параметры из query в sessionStorage.
 * Возвращает извлечённые параметры или null, если их нет.
 */
export function persistAttParams(search: string): AttParams | null {
  if (!isBrowser) {
    return null;
  }

  const params = extractAttParams(search);
  if (!Object.keys(params).length) {
    return null;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  }
  catch (error) {
    console.warn('Не удалось сохранить att параметры в sessionStorage', error);
  }

  return params;
}

/**
 * Читает сохранённые att_ параметры из sessionStorage.
 */
export function getStoredAttParams(): AttParams | null {
  if (!isBrowser) {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AttParams;
  }
  catch (error) {
    console.warn('Не удалось прочитать att параметры из sessionStorage', error);
    return null;
  }
}

export function clearAttParams(): void {
  if (!isBrowser) {
    return;
  }

  window.sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Проверяет, была ли в данной сессии уже отправка этого conversion/click payload.
 */
export function wasConversionClickSent(payload: AttParams | null | undefined): boolean {
  if (!isBrowser) {
    return false;
  }
  if (!payload) {
    return false;
  }

  return window.sessionStorage.getItem(SENT_FLAG_KEY) === getConversionClickPayloadKey(payload);
}

/**
 * Отмечает в sessionStorage, какой conversion/click payload был отправлен.
 */
export function setConversionClickSent(payload: AttParams): void {
  if (!isBrowser) {
    return;
  }
  try {
    window.sessionStorage.setItem(SENT_FLAG_KEY, getConversionClickPayloadKey(payload));
  }
  catch (error) {
    console.warn('Не удалось сохранить флаг отправки conversion/click', error);
  }
}
